import { useContext } from "react";
import { OnboardingCompletionContext } from "features/onboarding/guided/OnboardingBotSetup.context";
import { useLocation } from "react-router-dom";
import { useTradingIntent } from "features/onboarding/guided/intent";
import Steps from "common/Steps/Steps";
import styled, { css } from "styled-components";
import { Check } from "lucide-react";
import { hexToRGB } from "@shared/utils/misc";
import { reveal } from "features/onboarding/common/onboardingUi";
import { OnboardingStep } from "@shared/features/onboarding/types";
import Flex from "common/Boxes/Flex";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { STEP_TO_TITLE } from "@shared/features/onboarding/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import useQuery from "utils/hooks/useQuery";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import { WorkspaceType } from "@shared/types";

interface OnboardingStepsProps {
  currentStep?: OnboardingStep;
}

const OnboardingSteps = ({ currentStep }: OnboardingStepsProps) => {
  const { completed } = useContext(OnboardingCompletionContext);
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  // Phones: the journey as one line ("Step 1 of 2 · DCA bots"), not a row.
  const isPhone = useMedia([`(max-width: ${PHONE_STEPS}px)`], [true], false);
  const query = useQuery();
  const isDemo = useSelector(selectWorkspace) === WorkspaceType.DEMO;
  const { intent } = useTradingIntent();
  const location = useLocation();
  const guided = isDemo && query.get("guided") === "1";
  if (!guided && currentStep === 0) return null;

  if (intent) {
    const pathname = location.pathname;
    const reviewing = pathname.endsWith("overview");
    const selecting =
      pathname.endsWith("follow-signals-group") ||
      pathname.endsWith("copy-dca-bot") ||
      pathname.endsWith("connect-external-account");
    const titles =
      intent === "dca" || intent === "signals"
        ? ([
            isDemo ? "guided.step_account" : "intent.live_title",
            intent === "dca" ? "intent.dca.name" : "intent.signals.name",
            "guided.step_start",
          ] as const)
        : ([
            isDemo ? "guided.step_account" : "intent.live_title",
            "intent.how_it_works",
            "revision.setup_step",
          ] as const);
    const current = completed
      ? titles.length
      : query.get("setup") === "1"
      ? 2
      : reviewing
      ? 2
      : selecting || pathname.endsWith("trading-method")
      ? 1
      : 0;
    const shown = isDemo ? titles.slice(1) : titles;
    const index = Math.min(
      isDemo ? Math.max(0, current - 1) : current,
      shown.length - 1
    );
    if (isPhone)
      return (
        <JourneyLine aria-label={t("intent.selected")}>
          {t("intent.step_of", { n: index + 1, total: shown.length })}
          {/* Beside a back link only the count shows (OnboardingProvider). */}
          <span data-journey-section="">
            {" · "}
            {t(shown[index])}
          </span>
        </JourneyLine>
      );
    return (
      <JourneyProgress aria-label={t("intent.selected")}>
        {(isDemo ? titles.slice(1) : titles).map((title, index) => (
          <JourneyStep
            key={title}
            $done={index < (isDemo ? Math.max(0, current - 1) : current)}
            $current={(isDemo ? Math.max(0, current - 1) : current) === index}
            aria-current={
              (isDemo ? Math.max(0, current - 1) : current) === index
                ? "step"
                : undefined
            }
          >
            <strong>
              {index < (isDemo ? Math.max(0, current - 1) : current) ? (
                <Check size={12} strokeWidth={2.75} aria-hidden />
              ) : (
                index + 1
              )}
            </strong>
            <span>{t(title)}</span>
          </JourneyStep>
        ))}
      </JourneyProgress>
    );
  }
  const isMethodWalkthrough = guided && intent && intent !== "dca";
  const titles = isMethodWalkthrough
    ? (["guided.step_account", "intent.how_it_works"] as const)
    : guided
    ? ([
        "guided.step_account",
        "guided.step_preview",
        "guided.step_start",
      ] as const)
    : Object.values(STEP_TO_TITLE);
  const items = titles.map((stepTitle, index) => ({
    title: isMobile ? undefined : t(stepTitle),
    key: index,
    className: currentStep && index === currentStep - 1 ? "previous-step" : "",
  }));

  return (
    <Flex
      $justifyContent="center"
      style={{ maxWidth: isMobile ? 120 : 500, width: "100%" }}
    >
      <ProgressSteps
        size="small"
        titlePlacement="vertical"
        percent={isMobile ? undefined : 100}
        current={
          completed
            ? titles.length
            : isMethodWalkthrough
            ? location.pathname.endsWith("/connect-exchange")
              ? 0
              : 1
            : guided
            ? Math.max(0, (currentStep || 0) - 1)
            : currentStep
        }
        responsive={false}
        $isMobile={isMobile}
        items={items}
        styles={
          isMobile
            ? {
                itemIcon: {
                  width: 12,
                  height: 12,
                },
                itemRail: {
                  top: -6,
                  width: 20,
                  marginLeft: -10,
                },
              }
            : undefined
        }
      />
    </Flex>
  );
};

export default OnboardingSteps;

export const StyledOnboardingSteps = styled(Steps)<{ $isMobile: boolean }>`
  width: 100%;
  ${(p) =>
    p.$isMobile &&
    `
    .ant-steps-item-finish .ant-steps-item-icon {
      background: ${p.theme.colors.primaryBlue} !important;
    }
    .ant-steps-item-icon-number, .ant-steps-item-icon > span {
      display: none;
    }
    .ant-steps-item-process .ant-steps-item-wrapper:after {
      flex: none;
      border: 1px solid ${p.theme.colors.primaryBlue};
      content: "";
      display: inline-block;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      position: absolute;
      background: ${p.theme.colors.layoutBgColor};
      bottom: 5px;
    }
  `}
`;

/** Phones: the journey line, left-aligned with the step's title. */
const PHONE_STEPS = 560;
const JourneyLine = styled.p`
  width: 100%;
  margin: 0;
  font-size: 11px;
  line-height: 14px;
  font-weight: 520;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.secondaryText};
  ${reveal(0)};
`;
const JourneyProgress = styled.ol`
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 0;
  margin: 0;
  list-style: none;
  ${reveal(0)};
  @media (max-width: 850px) {
    gap: 8px;
  }
`;
const JourneyStep = styled.li<{ $current: boolean; $done: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: ${(p) => (p.$current ? 600 : 400)};
  color: ${(p) =>
    p.$current
      ? p.theme.colors.titleColor
      : p.$done
      ? p.theme.colors.primaryText
      : p.theme.colors.secondaryText};
  &:not(:first-child)::before {
    content: "";
    width: 28px;
    height: 1px;
    margin-right: 2px;
    background: ${(p) =>
      p.$done || p.$current
        ? hexToRGB(p.theme.colors.primaryBlue, 0.45)
        : p.theme.colors.cardBorderColor};
  }
  strong {
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    box-sizing: border-box;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 600;
    border: 1px solid
      ${(p) =>
        p.$current
          ? p.theme.colors.primaryBlue
          : p.$done
          ? "transparent"
          : p.theme.colors.lightBorderGrey};
    background: ${(p) =>
      p.$current
        ? p.theme.colors.primaryBlue
        : p.$done
        ? p.theme.colors.lightBlue
        : p.theme.colors.cardBgColor};
    color: ${(p) =>
      p.$current
        ? p.theme.colors.buttonText
        : p.$done
        ? p.theme.colors.primaryBlue
        : p.theme.colors.secondaryText};
    box-shadow: ${(p) =>
      p.$current ? `0 0 0 4px ${p.theme.colors.lightBlue}` : "none"};
  }
  @media (max-width: 850px) {
    span {
      display: none;
    }
    &:not(:first-child)::before {
      width: 20px;
    }
  }
`;

const ProgressSteps = styled(StyledOnboardingSteps)`
  ${(p) =>
    !p.$isMobile &&
    css`
      ${reveal(0)};
      &&& {
        --ant-cmp-steps-icon-size: 22px;
      }
      &&& .ant-steps-item-icon {
        font-size: 11px;
        font-weight: 600;
      }
      &&& .ant-steps-item-progress-icon-svg {
        display: none;
      }
      &&& .ant-steps-item-wait .ant-steps-item-icon {
        border-color: ${p.theme.colors.lightBorderGrey} !important;
        background: ${p.theme.colors.cardBgColor};
        color: ${p.theme.colors.secondaryText};
      }
      &&& .ant-steps-item-process .ant-steps-item-icon {
        border-color: ${p.theme.colors.primaryBlue} !important;
        background: ${p.theme.colors.primaryBlue};
        color: ${p.theme.colors.buttonText};
        box-shadow: 0 0 0 4px ${p.theme.colors.lightBlue};
      }
      &&& .ant-steps-item-finish .ant-steps-item-icon {
        border-color: transparent !important;
        background: ${p.theme.colors.lightBlue};
        color: ${p.theme.colors.primaryBlue};
      }
      &&& .ant-steps-item-rail-wait {
        border-color: ${p.theme.colors.cardBorderColor};
      }
      &&& .ant-steps-item-rail-process {
        border-color: ${hexToRGB(p.theme.colors.primaryBlue, 0.45)};
      }
      &&& .ant-steps-item-title {
        font-size: 13px;
        color: ${p.theme.colors.secondaryText};
      }
      &&& .ant-steps-item-finish .ant-steps-item-title {
        color: ${p.theme.colors.primaryText};
      }
      &&& .ant-steps-item-process .ant-steps-item-title {
        color: ${p.theme.colors.titleColor};
        font-weight: 600;
      }
    `}
`;
